import React, { useEffect, useRef, useState, useReducer, useMemo, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import Loading from '@/shared/Loading'
import axios from 'axios'
import { patch, get } from "@rails/request.js";
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { CheckCircleIcon, CloudUploadIcon, EmojiHappyIcon } from '@heroicons/react/solid'

// uuid
function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

// 6 digit string
function generateRandomString() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  for (let i = 0; i < 6; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  
  return result;
}

function createSlug(filename) {
  // Split the filename into base and extension
  const lastDotIndex = filename.lastIndexOf('.');
  let baseName, extension;
  
  if (lastDotIndex === -1) {
    // No extension found
    baseName = filename;
    extension = '';
  } else {
    baseName = filename.substring(0, lastDotIndex);
    extension = filename.substring(lastDotIndex);
  }
  
  // Clean up the base name
  const cleanBaseName = baseName
    .toLowerCase()
    .replace(/\s+/g, '-')       // Replace spaces with hyphens
    .replace(/[^\w\-\.]+/g, '') // Remove non-word chars except dots and hyphens
    .replace(/\-\-+/g, '-')     // Replace multiple hyphens with single hyphen
    .replace(/^-+/, '')         // Trim hyphens from start
    .replace(/-+$/, '');        // Trim hyphens from end
  
  // Return the cleaned base name with the original extension
  return extension ? cleanBaseName + extension : cleanBaseName;
}

const ProgressBar = ({ progressPercentage }) => {
  let color = 'bg-cccpurple-alt'
  if (progressPercentage > 10 && progressPercentage <= 30) {
    color = 'bg-gradient-to-r from-cccpurple-alt to-cccpurple'
  } else if (progressPercentage > 30 && progressPercentage <= 50) {
    color = 'bg-gradient-to-r from-cccpurple-alt via-cccpurple to-cccblue'
  } else if (progressPercentage > 50 && progressPercentage <= 70) {
    color = 'bg-gradient-to-r from-cccpurple via-cccblue to-cccblue-alt'
  } else if (progressPercentage > 70 && progressPercentage <= 90) {
    color = 'bg-gradient-to-r from-cccblue via-cccblue-alt to-cccorange'
  } else if (progressPercentage > 90) {
    color = 'bg-gradient-to-r from-cccblue via-cccorange to-cccorange-alt'
  }
  return (
    <div className='h-2 w-full bg-gray-300'>
      <div style={{ width: `${progressPercentage}%` }} className={`h-full ${color}`}> </div>
    </div>
  )
}

const AudioUploadForm = ({initialUrl, setUrl, kind}) => {
  const [, setToast] = useGlobalState('toast')
  const csrfToken = (document.head.querySelector('[name~=csrf-token]') || {}).content
  const [uploading, setUploading] = useState(false)
  const [baseMimeType, setBaseMimetype] = useState(null)
  const { getRequest, putpostRequest } = useQuery()
  const audioRef = useRef(null)
  const [progressPercentage, setProgressPercentage] = useState(0.0)
  const [file, setFile] = useState(null)

  // Samsung Browser's built-in privacy/anti-tracking features have a
  // documented history of breaking multipart XHR uploads — the request
  // goes out but the body is dropped, so both direct R2 PUT and our
  // server-proxy fallback fail with status:0 / progress_fired:false.
  // Showing this upfront saves users from a failed upload attempt.
  const isSamsungBrowser = typeof navigator !== 'undefined' && /SamsungBrowser/i.test(navigator.userAgent)

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">Accepted formats are mp3, wav, mov, and m4a. Max file size is 12MB. Wav files are typically large so we suggest converting to mp3.</p>
      </div>)
    }

    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    }
  }, [])


  const fileInputRef = useRef(null);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 12582912 // 12MB
  })

  const inputProps = getInputProps();

  const axiosConfig = {
    onUploadProgress: progressEvent => setProgressPercentage((progressEvent.loaded / progressEvent.total) * 100),
    headers: { 'X-CSRF-TOKEN': csrfToken }
  }

  const reset = () => {
    setFile(null)
    setProgressPercentage(0)
    setUploading(false)
    audioRef.current.src = initialUrl
  }


	const putToR2 = (signedUrl, fileToUpload) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let progressFired = false;
      xhr.upload.addEventListener("progress", (event) => {
        progressFired = true;
        if (event.lengthComputable) {
          setProgressPercentage((event.loaded / event.total) * 100)
        }
      });

      xhr.addEventListener("loadend", () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(true);
          } else {
            reject({ status: xhr.status, statusText: xhr.statusText, responseText: xhr.responseText, progressFired });
          }
        }
      });

      xhr.addEventListener("error", () => {
        reject({ status: 0, statusText: 'network error', responseText: '', progressFired });
      });

      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", fileToUpload.type);
      xhr.send(fileToUpload);
    });
  }

  // Worker-mode upload (prod): PUT the file straight to transmit.castingcall.club
  // with the signed token. The ccc-transmit Worker verifies it and writes to
  // R2 via a bucket binding — keeping the browser off the blocklisted
  // *.r2.cloudflarestorage.com host. Mirrors putToR2's resolve/reject shape.
  const putToTransmit = (uploadUrl, token, fileToUpload) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let progressFired = false;
      xhr.upload.addEventListener("progress", (event) => {
        progressFired = true;
        if (event.lengthComputable) {
          setProgressPercentage((event.loaded / event.total) * 100)
        }
      });

      xhr.addEventListener("loadend", () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(true);
          } else {
            reject({ status: xhr.status, statusText: xhr.statusText, responseText: xhr.responseText, progressFired });
          }
        }
      });

      xhr.addEventListener("error", () => {
        reject({ status: 0, statusText: 'network error', responseText: '', progressFired });
      });

      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("Content-Type", fileToUpload.type);
      xhr.send(fileToUpload);
    });
  }

  // Server-proxy upload: POST the file to ccc; the dyno PUTs it to R2.
  // Used as a final fallback after the direct PUT path exhausts all
  // retries. Mirrors putToR2's resolve/reject shape so callers can
  // treat both paths uniformly.
  const proxyUpload = (file, key, contentType, kind) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const startedAt = Date.now()
      let progressFired = false
      xhr.upload.addEventListener("progress", (event) => {
        progressFired = true
        if (event.lengthComputable) {
          setProgressPercentage((event.loaded / event.total) * 100)
        }
      })
      xhr.addEventListener("loadend", () => {
        const durationMs = Date.now() - startedAt
        if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
          try {
            const body = JSON.parse(xhr.responseText)
            resolve({ url: body.url, durationMs, status: xhr.status })
          } catch (e) {
            reject({ status: xhr.status, statusText: 'bad response', progressFired, durationMs })
          }
        } else {
          reject({ status: xhr.status, statusText: xhr.statusText || 'proxy error', progressFired, durationMs })
        }
      })
      xhr.addEventListener("error", () => {
        reject({ status: 0, statusText: 'network error', progressFired, durationMs: Date.now() - startedAt })
      })

      const form = new FormData()
      form.append('file', file)
      form.append('key', key)
      form.append('kind', kind)
      form.append('content_type', contentType)
      xhr.open("POST", '/api/v3/audio_uploads/r2_proxy_upload', true)
      xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken)
      xhr.send(form)
    })
  }

  // Map captured signals to a user-facing message. Prioritized so the most
  // specific diagnosis wins.
  const diagnosisMessage = (d) => {
    if (d.cspViolation) {
      return 'Your browser blocked this upload for security reasons. Try a different browser.'
    }
    if (!d.online) {
      return 'You appear to be offline. Reconnect to the internet and try again.'
    }
    if (d.serviceWorker && !d.progressFired) {
      return 'Your browser is using an outdated version of the page. Close the tab/app, reopen it, and try again.'
    }
    if (d.progressFired) {
      return 'The upload was interrupted partway through. Try switching networks (Wi-Fi ↔ cellular) and try again.'
    }
    if (d.status === 0) {
      return "Your network appears to be blocking uploads. This is common on office, school, and some carriers' networks. Try a different network."
    }
    return `Upload failed (status ${d.status}). Please try again or contact support@castingcall.club`
  }

	const uploadFile = async () => {
    setUploading(true)
    var key = `audio-${generateRandomString()}-${createSlug(file.name)}`
    var contentType = file.type

    // Listen for any CSP violation that fires during the upload window — if
    // browser strict-mode is blocking the R2 host, this is the signal.
    let cspViolation = null
    const onCsp = (e) => {
      cspViolation = { blockedURI: e.blockedURI, violatedDirective: e.violatedDirective }
    }
    document.addEventListener('securitypolicyviolation', onCsp)

    try {
      const response = await get(`/api/v3/audio_uploads/r2_hash?key=${key}&kind=${kind}&content_type=${contentType}`, {
        responseKind: "application/json",
      })

      if (response.ok) {
        const json = await response.json
        console.log('uploading to:', json.signed_url)

        const maxRetries = 2
        let lastError = null
        let verifyUrl = null
        let proxyAttempted = false
        let proxyStatus = null
        let proxyDurationMs = null

        // Worker mode (prod): PUT straight to transmit.castingcall.club with a
        // signed token; the Worker writes to R2. Legacy mode (dev): presigned
        // PUT direct to R2. Detected by the shape of the r2_hash response.
        const workerMode = !!(json.upload_url && json.token)

        // Direct PUT path — unchanged happy path for the 99%. No pre-flight,
        // no extra latency.
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            if (attempt > 0) {
              console.log(`Retrying upload (attempt ${attempt + 1}/${maxRetries + 1})...`)
              setProgressPercentage(0)
              await new Promise(r => setTimeout(r, 1000 * attempt)) // 1s, then 2s delay
            }
            if (workerMode) {
              await putToTransmit(json.upload_url, json.token, file)
            } else {
              await putToR2(json.signed_url, file)
            }
            lastError = null
            verifyUrl = workerMode ? json.public_url : `${json.base_url}${key}`
            break
          } catch (err) {
            lastError = err
            console.error(`Upload attempt ${attempt + 1} failed:`, err.status, err.statusText)
          }
        }

        // Server-proxy fallback — only triggers after all direct-PUT retries
        // are exhausted. The 99% who succeed via direct never see this.
        if (!verifyUrl) {
          proxyAttempted = true
          setProgressPercentage(0)
          try {
            const proxyResult = await proxyUpload(file, key, contentType, kind)
            verifyUrl = proxyResult.url
            proxyStatus = proxyResult.status
            proxyDurationMs = proxyResult.durationMs
            lastError = null
          } catch (err) {
            proxyStatus = err.status
            proxyDurationMs = err.durationMs
            // Preserve the direct-PUT error if there was one — both failures
            // share the diagnostic toast.
            lastError = lastError || err
            console.error('Proxy upload failed:', err.status, err.statusText)
          }
        }

        if (!verifyUrl) {
          // Best-effort R2 host capture for the diagnostic payload.
          let r2Host = ''
          try { r2Host = new URL(json.upload_url || json.signed_url).host } catch (_) {}

          // Both paths exhausted. Report rich diagnostics and surface a
          // user-facing message tailored to the most likely cause.
          const diagnosis = {
            status: lastError?.status ?? 0,
            progressFired: lastError?.progressFired || false,
            online: navigator.onLine,
            cspViolation,
            serviceWorker: !!(navigator.serviceWorker && navigator.serviceWorker.controller),
            userAgent: navigator.userAgent,
            effectiveType: navigator.connection?.effectiveType || '',
            fileType: file.type || 'empty',
            fileSize: file.size,
            filename: file.name
          }
          const reportData = new URLSearchParams({
            status: diagnosis.status,
            progress_fired: diagnosis.progressFired,
            online: diagnosis.online,
            csp_violation: diagnosis.cspViolation ? JSON.stringify(diagnosis.cspViolation) : '',
            service_worker: diagnosis.serviceWorker,
            user_agent: diagnosis.userAgent,
            effective_type: diagnosis.effectiveType,
            content_type: diagnosis.fileType,
            file_size: diagnosis.fileSize,
            filename: diagnosis.filename,
            r2_host: r2Host,
            proxy_attempted: proxyAttempted,
            proxy_status: proxyStatus === null ? '' : String(proxyStatus),
            proxy_duration_ms: proxyDurationMs === null ? '' : String(proxyDurationMs)
          })
          fetch(`/api/v3/audio_uploads/r2_upload_error?${reportData}`, {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': csrfToken }
          }).catch(() => {})

          setUploading(false)
          setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium dark:text-red-100 text-red-600">Upload Error</p>
            <p className="mt-1 text-sm dark:text-red-300 text-red-400">{diagnosisMessage(diagnosis)}</p>
            <p className="mt-2 text-xs dark:text-red-300 text-red-400">
              <b>Quick fix to try first:</b> open this page in a private/incognito window and retry. That disables browser extensions, which are the most common cause of this error.
            </p>
            <p className="mt-2 text-xs dark:text-red-300 text-red-400">
              If that still fails, please screenshot this error and email <b>support@castingcall.club</b> with:
              <br />1. Does it fail on both Wi-Fi <i>and</i> cellular?
              <br />2. Do you have any ad-blocker, privacy, or DNS app installed?
              <br />3. Which country and carrier/ISP are you on?
            </p>
          </div>)
          return
        }

        setUploading(false)
        setUrl(verifyUrl)
      } else {
        console.log('Session expired or server error, please refresh the page');
        setUploading(false);

        const json = await response.json;
        setUploading(false);
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-red-100 text-red-600">A shucks. An error</p>
          <p className="mt-1 text-sm dark:text-red-300 text-red-400">{json.errors}</p>
        </div>)

      }
    } catch (error) {
      console.error('error', error)
      setUploading(false)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Big Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">{error} If you see this, report it to support@castingcall.club</p>
      </div>)
    } finally {
      document.removeEventListener('securitypolicyviolation', onCsp)
    }
  }

	const uploadFile2 = async () => {
    setUploading(true)
    var key = `audio-${generateRandomString()}-${createSlug(file.name)}`
		const response = await get(`/api/v3/audio_uploads/r2_hash?key=${key}`, {
			responseKind: "application/json",
		})

		if (response.ok) {
      const json = await response.json
      console.log('uploading', json.url)

      var data = new FormData()
      data.append('Content-Type', file.type)
      data.append('Content-Encoding', 'base64')
      data.append('file', file)

      // using xhr because fetch doesnt support progress yet
      const xhr = new XMLHttpRequest();
      const success = await new Promise((resolve) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            setProgressPercentage((event.loaded / event.total) * 100)
          }
        });
        xhr.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            console.log("download progress:", event.loaded / event.total);
          }
        });
        xhr.addEventListener("loadend", () => {
          resolve(xhr.readyState === 4 && xhr.status === 200);
          setUploading(false)
          setUrl(json.base_url + '/' + key)
        });
        xhr.open("PUT", json.signed_url, true);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });
    } else {
      console.log('refresh the page. your session is expired')
      setUploading(false)
    }
  }

  useEffect(() => {
    if (!file) { return }

    let mimeType = file.type || '';
    console.log('Selected file type:', mimeType, 'name:', file.name);
    
    // Extract file extension
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    // Consider both MIME type and file extension
    const isAudioFile = 
      mimeType.startsWith('audio/') || 
      ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(fileExt);
    
    // Allow certain video extensions that can contain audio
    const isSupportedVideoFile = 
      mimeType.startsWith('video/') || 
      ['mp4', 'mov'].includes(fileExt);
    
    if (!isAudioFile && !isSupportedVideoFile) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">
          Accepted formats are mp3, wav, m4a, mp4, and mov. Max file size is 12MB. 
          Wav files are typically large so we suggest converting to mp3.
        </p>
      </div>)
      setFile(null) // Reset file selection
      setUploading(false)
      return
    }
    
    // Create object URL for audio preview
    try {
      audioRef.current.src = URL.createObjectURL(file)
    } catch (error) {
      console.error('Error creating object URL:', error)
    }
  }, [file])

  return <>
    <div className="relative p-8">
      <div className="sm:text-center">
        <h2 className="text-3xl dark:text-white font-extrabold text-slate-900 tracking-tight sm:text-4xl">Upload Audio</h2>
      </div>
      <div className='w-70 sm:w-96 mx-auto mt-20'>
        { isSamsungBrowser && (
          <div className='mb-3 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 p-3'>
            <p className='text-sm font-medium text-amber-800 dark:text-amber-200'>Samsung Browser detected</p>
            <p className='mt-1 text-xs text-amber-700 dark:text-amber-300'>
              Samsung Browser has known issues uploading files. If your upload fails, please open this page in <b>Chrome</b> or <b>Firefox</b> and try again.
            </p>
          </div>
        )}
        <audio ref={audioRef} controls className='h-10 w-96 mx-auto object-cover rounded-md shadow-lg border border-2 border-black' src={initialUrl} />
        { !file && <div className="bg-gray-50 dark:bg-gray-750 mt-3 relative block min-h-20 w-full border-2 border-gray-300 flex flex-col justify-center cursor-pointer border-dashed rounded-lg p-12 text-center hover:border-gray-400" {...getRootProps()} >
          <input 
            ref={fileInputRef}
            {...inputProps} 
            // Use the standard HTML accept attribute which works on iOS
            accept="audio/*,.mp3,.wav,.m4a,.aac,.mp4,.mov"
            // Ensure no autocomplete attempts by browser
            autoComplete="off"
            onClick={(e) => {
              console.log('File input clicked');
              // iOS Safari can be picky about event bubbling
              e.stopPropagation();
            }}
          />
          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300">
            <CloudUploadIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300">
              Click or drop <b>audio</b> file to upload.
              <p className='text-xs'>If your file is mp3, we will not alter it. If it's another format, we will convert it to mp3.</p>
              <p className='text-xs'>For best results, <b>mp3 format, max bitrate 192 kps, 44.1 or 48KHz, Mono</b></p>
            </span>
          </span>
        </div> }
        { !file && <div className="rounded-md bg-cccpurple dark:bg-cccorange bg-opacity-20 dark:bg-opacity-20 p-4 mt-2">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-cccpurple dark:text-cccorange" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M19 10.5a8.5 8.5 0 11-17 0 8.5 8.5 0 0117 0zM8.25 9.75A.75.75 0 019 9h.253a1.75 1.75 0 011.709 2.13l-.46 2.066a.25.25 0 00.245.304H11a.75.75 0 010 1.5h-.253a1.75 1.75 0 01-1.709-2.13l.46-2.066a.25.25 0 00-.245-.304H9a.75.75 0 01-.75-.75zM10 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1 md:flex md:justify-between">
              <p className="text-sm text-cccpurple dark:text-cccorange uppercase">The project owner can listen to your original <b><i>uncompressed</i></b> raw audio file at any time. Nice!</p>
            </div>
          </div>
        </div> }

        { file && <div className='bg-gray-50 w-full mt-2 border-2 rounded-lg border-gray-300 p-2'>
          <div className='mt-2 text-sm text-gray-500 dark:text-gray-400 break-words'>{file.name} - {(file.size / 1000 / 1000).toFixed(2)} MB</div>
          { uploading && <ProgressBar progressPercentage={progressPercentage} /> }
          <div className='mt-2 flex justify-center gap-x-3'>
            <button onClick={reset} className="mt-2 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-md bg-ssbeige px-3 py-2 text-slate-900 border-2 border-black">
              <span className="absolute h-0 w-0 rounded-full bg-ssbeige-alt transition-all duration-300 group-hover:h-56 group-hover:w-32"></span>
              <span className="relative">
                Reset
              </span>
            </button>
            <button onClick={uploadFile} className="mt-2 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-md bg-cccblue px-3 py-2 text-white border-2 border-black">
              <span className="absolute h-0 w-0 rounded-full bg-cccpurple transition-all duration-300 group-hover:h-56 group-hover:w-32"></span>
              <span className="relative">
                Save
              </span>
            </button>
          </div>
        </div> }
      </div>
    </div>
  </>
}

export default AudioUploadForm
